/**
 * Sentry browser SDK initialization with HIPAA-grade PHI scrubbing.
 *
 * Imported at the very top of `App.js` so it instruments React + the
 * router + fetch/XHR before any user code runs. No-ops cleanly when
 * `REACT_APP_SENTRY_DSN` is empty (preview/dev).
 *
 * HIPAA notes:
 *  - Session Replay is DISABLED via `replaysSessionSampleRate=0` and
 *    `replaysOnErrorSampleRate=0`. Replays would screen-record the
 *    wizard and capture PHI directly into Sentry's DOM snapshots.
 *  - `sendDefaultPii=false` keeps default identifying headers off the
 *    wire even before the scrubber runs.
 *  - The `beforeSend` hook scrubs request bodies, breadcrumb data, and
 *    exception values for PHI keys (member_id, dob, ssn, address, etc.)
 *    before any HTTPS call to Sentry leaves the browser.
 */
import * as Sentry from "@sentry/react";

const PHI_KEY_PATTERNS = [
  /client[_ ]?name/i, /patient[_ ]?name/i,
  /first[_ ]?name/i, /last[_ ]?name/i, /full[_ ]?name/i,
  /member[_ ]?id/i, /medicaid[_ ]?id/i, /forwardhealth[_ ]?id/i,
  /\bssn\b/i, /social[_ ]?security/i,
  /\bdob\b/i, /date[_ ]?of[_ ]?birth/i, /birth[_ ]?date/i,
  /\baddress\b/i, /\bstreet\b/i, /\bzip\b/i, /postal/i,
  /\bphone\b/i, /\bmobile\b/i,
  /clinical[_ ]?(notes?|narrative)/i, /medical[_ ]?notes?/i,
  /\bdiagnosis\b/i, /medication[_ ]?list/i,
  /caregiver[_ ]?name/i,
];

const PHI_URL_PATTERNS = [
  /[?&]member_id=/i, /[?&]client_name=/i, /[?&]dob=/i, /[?&]ssn=/i,
];

const REDACT_STR = "[REDACTED-PHI]";

function isPhiKey(key) {
  if (typeof key !== "string") return false;
  for (const p of PHI_KEY_PATTERNS) if (p.test(key)) return true;
  return false;
}

function redactValue(value) {
  if (typeof value === "string") return REDACT_STR;
  if (Array.isArray(value)) return [REDACT_STR];
  if (value && typeof value === "object") return { _redacted: "phi" };
  return REDACT_STR;
}

function scrub(obj, depth = 0) {
  if (depth > 8 || obj == null) return obj;
  if (Array.isArray(obj)) return obj.map((x) => scrub(x, depth + 1));
  if (typeof obj === "object") {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      out[k] = isPhiKey(k) ? redactValue(v) : scrub(v, depth + 1);
    }
    return out;
  }
  return obj;
}

function scrubUrl(url) {
  if (typeof url !== "string") return url;
  for (const p of PHI_URL_PATTERNS) {
    if (p.test(url)) {
      const base = url.split("?")[0];
      return `${base}?[REDACTED-PHI-QUERYSTRING]`;
    }
  }
  return url;
}

function beforeSend(event /*, hint */) {
  try {
    // 1. Request data
    if (event.request) {
      if (event.request.url) event.request.url = scrubUrl(event.request.url);
      if (event.request.query_string) {
        event.request.query_string = scrubUrl(`?${event.request.query_string}`).replace(/^\?/, "");
      }
      for (const k of ["data", "headers", "cookies", "env"]) {
        if (event.request[k]) event.request[k] = scrub(event.request[k]);
      }
    }

    // 2. Exception values — strip PHI key-value-looking strings
    if (event.exception?.values) {
      for (const ex of event.exception.values) {
        if (typeof ex.value === "string") {
          for (const p of PHI_KEY_PATTERNS) {
            if (p.test(ex.value)) {
              ex.value = `${ex.type || "Error"}: [message redacted: contained PHI key]`;
              break;
            }
          }
        }
      }
    }

    // 3. Breadcrumbs
    if (Array.isArray(event.breadcrumbs)) {
      for (const c of event.breadcrumbs) {
        if (c.data) c.data = scrub(c.data);
        if (typeof c.message === "string") c.message = scrubUrl(c.message);
      }
    }

    // 4. Top-level scopes
    for (const k of ["tags", "extra", "contexts"]) {
      if (event[k]) event[k] = scrub(event[k]);
    }

    // 5. User — keep only id / ip_address; drop anything else
    if (event.user) {
      const safe = {};
      for (const k of ["id", "ip_address"]) {
        if (event.user[k]) safe[k] = event.user[k];
      }
      if (Object.keys(safe).length) event.user = safe;
      else delete event.user;
    }

    return event;
  } catch (e) {
    // If scrubbing crashed, drop the event — better to lose diagnostics
    // than to leak PHI.
    // eslint-disable-next-line no-console
    console.warn("Sentry PHI scrubber crashed — dropping event:", e);
    return null;
  }
}

export function initSentry() {
  const dsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();
  if (!dsn) {
    return false;
  }

  const env = (process.env.REACT_APP_SENTRY_ENVIRONMENT || "preview").trim();
  const release = (process.env.REACT_APP_SENTRY_RELEASE || "").trim() || undefined;
  const sample = parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.05");

  Sentry.init({
    dsn,
    environment: env,
    release,
    sendDefaultPii: false,
    tracesSampleRate: isNaN(sample) ? 0.05 : sample,
    // PHI risk — Session Replay records DOM. NEVER enable on this app.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    beforeSend,
    beforeBreadcrumb(breadcrumb /*, hint */) {
      // Drop console + navigation breadcrumbs entirely — they're the
      // most common PHI-leak vectors (we log client_name in dev to the
      // console occasionally).
      if (breadcrumb.category === "console") return null;
      // Strip http breadcrumbs down to method + status
      if (["fetch", "xhr"].includes(breadcrumb.category)) {
        return {
          ...breadcrumb,
          data: {
            method: breadcrumb.data?.method,
            status_code: breadcrumb.data?.status_code,
            url: scrubUrl(breadcrumb.data?.url || ""),
          },
        };
      }
      return breadcrumb;
    },
  });
  return true;
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
